import { AxiosError } from "axios";

import type { LoginRequest } from "@/backend/http/auth";
import Auth from "@/backend/http/auth";
import { handleError } from "@/error/errorHandler";
import { errorMessage } from "@/error/errorUnwrapper";
import { addBreadcrumb, captureMessage } from "@/error/sentry";
import type { Tenant } from "@/model/session";
import { neverResolve } from "@/utils/async/utils";

export async function login(request: LoginRequest, reason?: string): Promise<Tenant[]> {
  let res;
  try {
    res = await Auth.login(request);
    addBreadcrumb("login", { data: { reason } });
  } catch (err) {
    const message = handleLoginError(err);
    if (!isWrongEmail(message)) {
      captureMessage(`Unauthorized to log in: ${message}`);
    }
    throw Error(message, { cause: err });
  }

  if (!res.data.success) {
    const errorMessage = res.data.error;
    captureMessage("Error while trying to log in", { response: { error: res.data.error } });
    throw Error(errorMessage);
  }

  if (res.data.redirectUrl) {
    window.location.assign(res.data.redirectUrl);
    return neverResolve();
  }

  if (res.data.tenants) {
    return res.data.tenants;
  }

  throw Error("No data returned while trying to log in");
}

function handleLoginError(err: unknown): string {
  if (err instanceof AxiosError) {
    if (err.response?.status !== 401) {
      handleError(err, "Unexpected error while trying to log in");
    }
    const dataError = err.response?.data?.error;
    if (typeof dataError === "string") {
      return dataError;
    }
  }
  return errorMessage(err, "Could not log in");
}

function isWrongEmail(errorMessage: string) {
  return errorMessage === "Invalid email" || errorMessage === "Invalid login";
}
